import type { KbFolder } from '../services/kbApi';

export interface FolderRow {
  folder: KbFolder;
  hasChildren: boolean;
  open: boolean;
}

/** Path of each folder's parent, from the depth-ordered list the API returns. */
export function parentPaths(folders: KbFolder[]): Map<string, string | null> {
  const parents = new Map<string, string | null>();
  const stack: KbFolder[] = [];
  for (const f of folders) {
    while (stack.length && stack[stack.length - 1].depth >= f.depth) stack.pop();
    parents.set(f.path, stack.length ? stack[stack.length - 1].path : null);
    stack.push(f);
  }
  return parents;
}

/** Folders whose ancestors are all open, with their chevron state. */
export function visibleFolderRows(folders: KbFolder[], open: Set<string>): FolderRow[] {
  const parents = parentPaths(folders);
  const hasKids = new Set<string>();
  parents.forEach((p) => { if (p) hasKids.add(p); });
  const isVisible = (path: string): boolean => {
    let p = parents.get(path) ?? null;
    while (p) {
      if (!open.has(p)) return false;
      p = parents.get(p) ?? null;
    }
    return true;
  };
  return folders
    .filter((f) => isVisible(f.path))
    .map((f) => ({ folder: f, hasChildren: hasKids.has(f.path), open: open.has(f.path) }));
}

/** Default open set: top-level folders, plus every ancestor of the selected folder. */
export function initialOpenFolders(folders: KbFolder[], active: string): Set<string> {
  const open = new Set(folders.filter((f) => f.depth === 0).map((f) => f.path));
  const parents = parentPaths(folders);
  let p = active ? parents.get(active) ?? null : null;
  while (p) {
    open.add(p);
    p = parents.get(p) ?? null;
  }
  return open;
}
